<script lang="ts" setup generic="T extends string | number">
  import type { ButtonInputOption } from '~/core/types'

  type Props = {
    disabled?: boolean
    multiple?: boolean
    width?: number
    options: ButtonInputOption<T>[]
  }

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    multiple: true,
    width: 100,
  })

  const model = defineModel<Array<T>>({ required: true })

  function handleInput(option: ButtonInputOption<T>): void {
    if (!props.multiple) {
      model.value = [option.value]
      return
    }
    const proxy = [...model.value]
    const index = proxy.findIndex((value) => value === option.value)
    if (index !== -1) {
      proxy.splice(index, 1)
    } else {
      proxy.push(option.value)
    }
    model.value = proxy
  }

  function isActive(option: ButtonInputOption<T>): boolean {
    return model.value.findIndex((value) => value === option.value) !== -1
  }

</script>

<template>
  <div class="c-buttons-input">
    <button
      v-for="(option, k) of options"
      :key="k"
      class="button"
      :class="{ active: isActive(option) }"
      :style="{ width: `${width}px` }"
      @click="() => handleInput(option)"
    >
      <span class="label">
        <slot
          v-if="$slots['option']"
          name="option"
          :option="option"
        />
        <template v-else>
          {{ option ? option.label : "" }}
        </template>
      </span>
    </button>
  </div>
</template>

<style scoped>

.c-buttons-input {
  display: flex;
  flex-flow: row wrap;

  .button {
    width: 100px;
    flex: 0 0 auto;
    padding: 0.375rem 0;
    border: solid 1px rgb(26 44 54 / 20%);
    border-radius: 4px;
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;

    .label {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: inherit;
      font-weight: normal;
      opacity: 0.8;
      white-space: nowrap;
    }

    &.active {
      border: solid 1px var(--color-primary);
      background-color: var(--color-primary-light);

      .label {
        color: var(--color-primary);
        font-weight: 600;
      }
    }
  }
}
</style>
